import React, {useState, useEffect, FC} from 'react';
import {Calendar} from '@epic-ui/icons';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import moment from 'moment';

import {
    DayRangePicker,
    WeekRangePicker,
    MonthRangePicker,
    InfoPanel,
    YearMonthPicker,
    useRangePickerData,
    GlobalSelectButton,
    CategorizedDropdown
} from '../../components';
import {UpdateButton} from '../update-button';
import DiscretRangePicker from './DiscretRangePicker';
import TimeRangeButton from './TimeRangeButton';
import CalendarHeaderButton from './CalendarHeaderButton';
import DatePickerContainer from './DatePickerContainer';
import {
    getDefaultTimeRange,
    getTimeRangeDates,
    getDateline,
    isPreviousMonthMonday,
    isUniqueDateRangeForCategory,
    isFuture,
    getYearMonthDate
} from './utils';
import {TIME_SCALES} from '../constants';
import Ellipsis from '../../components/common/Ellipsis';
import useTimeRangeWarning from '../v2/sections/fortnite-games/components/time-range-limit/useTimeRangeWarning';

import {TimeRange, TimeScale, DateMoment} from '../../types';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';
import {DatePickerProps} from '../common/date-range-picker/types';

const CATEGORY_TAB_ORDER = TIME_SCALES.slice(1);
const INITIAL_TIME_RANGE = TimeRange.FORTNIGHT;
const INITIAL_CATEGORY = TimeScale.DAILY;

export const CODES = {
    [TimeScale.DAILY]: 'epic.affiliate.fortnite_games.date_select.daily_players',
    [TimeScale.WEEKLY]: 'epic.affiliate.fortnite_games.date_select.weekly_players',
    [TimeScale.MONTHLY]: 'epic.affiliate.fortnite_games.date_select.monthly_players',
    update: 'epic.affiliate.fortnite_games.update_date',
    today: 'epic.affiliate.fortnite_games.date_select.today',
    customSelection: 'epic.affiliate.fortnite_games.game_select.custom_selection',
    customTimeRange: 'epic.affiliate.fortnite_games.discret_range_picker.custom_time_range',
    customTimeRangeTooltip: 'epic.affiliate.fortnite_games.discret_range_picker.time_range.tooltip',
    infoMessage: 'epic.affiliate.fortnite_games.date_range_picker.calendar.message'
};

export interface GlobalDatePickerProps extends DatePickerProps {
    getMessage?: (code: string) => string;
}

const GlobalDatePicker: FC<GlobalDatePickerProps> = ({
    selectedDate: selected,
    startDate: start,
    endDate: end,
    onRangeChange,
    onTimeScaleChange,
    getMessage
}: GlobalDatePickerProps) => {
    const {
        isPreselection,
        startDate,
        endDate,
        selectedDate,
        setIsPreselection,
        handleDayChange,
        handleWeekChange,
        handleMonthChange,
        handleYearMonthChange,
        setCalendar
    } = useRangePickerData({
        selectedDate: selected,
        startDate: start,
        endDate: end
    });

    const [anchorEl, setAnchorEl] = useState(null);
    const [isChanged, setIsChanged] = useState(false);
    const [isCalendar, setIsCalendar] = useState(false);
    const [isYearMonthCalendar, setIsYearMonthCalendar] = useState(false);

    const [activeCategory, setActiveCategory] = useState(INITIAL_CATEGORY);
    const [previousCategory, setPreviousCategory] = useState(activeCategory);
    const [activeTimeRange, setActiveTimeRange] = useState(INITIAL_TIME_RANGE);
    const [previousTimeRange, setPreviousTimeRange] = useState(activeTimeRange);
    const [previousTimeRangeDates, setPreviousTimeRangeDates] = useState(
        getTimeRangeDates(INITIAL_TIME_RANGE, INITIAL_CATEGORY)
    );

    const open = Boolean(anchorEl);
    const id = open ? 'global-date-picker' : undefined;

    const theme = useTheme();
    const isFsmBreakpoint = useMediaQuery(theme.breakpoints.up('sm'));
    const [checkTimeRange] = useTimeRangeWarning();

    const customTimeRangeButtonTitle = getMessage(CODES.customTimeRange);
    const customTimeRangeButtonTooltipTitle = getMessage(CODES.customTimeRangeTooltip);

    const dateline = getDateline({startDate, endDate, message: getMessage(CODES.today)});

    const handleDayRangeChange = (date: DateMoment) => {
        setIsChanged(true);
        handleDayChange(date);
    };

    const handleWeekRangeChange = (date: DateMoment) => {
        setIsChanged(true);
        handleWeekChange(date);
    };

    const handleWeekClick = (date: DateMoment) => {
        if (isPreviousMonthMonday(date)) {
            setIsChanged(true);
            handleWeekChange(date);
        }
    };

    const handleMonthRangeChange = (date: DateMoment) => {
        setIsChanged(true);
        handleMonthChange(date);
    };

    const handleYearMonthRangeChange = (date: moment.Moment) => {
        const selectedMonth = isFuture(date) ? getYearMonthDate(activeCategory) : date;

        handleYearMonthChange(selectedMonth);
    };

    const handleDiscretRangeChange = (_event, timeRange) => {
        setActiveTimeRange(timeRange);

        if (timeRange === TimeRange.TIME_RANGE) {
            setIsCalendar(true);
            setIsPreselection(true);
        } else {
            const activeTimeRangeDates = getTimeRangeDates(timeRange, activeCategory);
            const isUniqueDateRange = isUniqueDateRangeForCategory(
                previousTimeRangeDates,
                activeTimeRangeDates,
                previousCategory,
                activeCategory
            );

            setCalendar(activeTimeRangeDates);
            setIsChanged(isUniqueDateRange);
        }
    };

    const handleCategoryChange = (_event, index) => {
        const activeCategory = CATEGORY_TAB_ORDER[index];
        const defaultTimeRange = getDefaultTimeRange(activeCategory);
        const defaultTimeRangeDates = getTimeRangeDates(defaultTimeRange, activeCategory);
        const isUniqueDateRange = isUniqueDateRangeForCategory(
            previousTimeRangeDates,
            defaultTimeRangeDates,
            previousCategory,
            activeCategory
        );

        setActiveCategory(activeCategory);
        setActiveTimeRange(defaultTimeRange);
        setCalendar(defaultTimeRangeDates);
        setIsCalendar(false);
        setIsChanged(isUniqueDateRange);
    };

    const handleYearMonthCalendarSwitch = () => {
        setIsYearMonthCalendar(!isYearMonthCalendar);
    };

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseTimeRangeClick = event => {
        setIsCalendar(false);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setIsChanged(false);
    };

    const reset = () => {
        setActiveCategory(previousCategory);
        setActiveTimeRange(previousTimeRange);
        setCalendar({startDate: start, endDate: end, selectedDate: selected});
        setIsYearMonthCalendar(false);
        setIsCalendar(false);
    };

    const handleUpdate = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        checkTimeRange(
            {startDate, endDate, timeScale: activeCategory},
            {
                onConfirm: () => {
                    setPreviousCategory(activeCategory);
                    setPreviousTimeRange(activeTimeRange);
                    setPreviousTimeRangeDates({startDate, endDate, selectedDate});
                    onRangeChange({startDate, endDate, selectedDate});
                    onTimeScaleChange(activeCategory);

                    setIsCalendar(false);

                    handleClose();
                },
                onCancel: reset
            }
        );
    };

    useEffect(() => {
        if (!open) {
            reset();
        }
    }, [open]);

    const updateButtonSize = isFsmBreakpoint ? 'large' : 'medium';

    return (
        <>
            <GlobalSelectButton
                startAdornment={<Calendar />}
                endAdornment={
                    isChanged ? (
                        <UpdateButton size={updateButtonSize} onClick={handleUpdate}>
                            {getMessage(CODES.update)}
                        </UpdateButton>
                    ) : null
                }
                open={open}
                onClick={handleClick}>
                <Ellipsis>{dateline}</Ellipsis>
            </GlobalSelectButton>
            <CategorizedDropdown
                hideCategoryContentButton={isCalendar}
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                value={CATEGORY_TAB_ORDER.indexOf(activeCategory)}
                onChange={handleCategoryChange}
                ContentBoxProps={{m: 0}}>
                <CategorizedDropdown.Category label={getMessage(CODES[TimeScale.DAILY])}>
                    {isCalendar ? (
                        <DatePickerContainer>
                            <TimeRangeButton
                                title={customTimeRangeButtonTitle}
                                tooltipTitle={customTimeRangeButtonTooltipTitle}
                                onClick={handleCloseTimeRangeClick}
                            />
                            {isYearMonthCalendar ? (
                                <YearMonthPicker
                                    startDate={start}
                                    endDate={end}
                                    value={selectedDate}
                                    onChange={handleYearMonthRangeChange}
                                    onMonthChange={handleYearMonthCalendarSwitch}
                                    onCalendarSwitch={handleYearMonthCalendarSwitch}
                                />
                            ) : (
                                <CalendarHeaderButton onClick={handleYearMonthCalendarSwitch}>
                                    <DayRangePicker
                                        autoOk
                                        disableToolbar
                                        selectedDate={selectedDate}
                                        startDate={startDate}
                                        endDate={endDate}
                                        variant="static"
                                        openTo="date"
                                        value={selectedDate}
                                        onChange={handleDayRangeChange}
                                        isPreselection={isPreselection}
                                    />
                                </CalendarHeaderButton>
                            )}
                        </DatePickerContainer>
                    ) : (
                        <DatePickerContainer>
                            <DiscretRangePicker
                                value={activeTimeRange}
                                category={activeCategory}
                                onChange={handleDiscretRangeChange}
                            />
                        </DatePickerContainer>
                    )}
                </CategorizedDropdown.Category>
                <CategorizedDropdown.Category label={getMessage(CODES[TimeScale.WEEKLY])}>
                    {isCalendar ? (
                        <DatePickerContainer>
                            <TimeRangeButton
                                title={customTimeRangeButtonTitle}
                                tooltipTitle={customTimeRangeButtonTooltipTitle}
                                onClick={handleCloseTimeRangeClick}
                            />
                            {isYearMonthCalendar ? (
                                <YearMonthPicker
                                    startDate={start}
                                    endDate={end}
                                    value={selectedDate}
                                    onChange={handleYearMonthRangeChange}
                                    onMonthChange={handleYearMonthCalendarSwitch}
                                    onCalendarSwitch={handleYearMonthCalendarSwitch}
                                />
                            ) : (
                                <CalendarHeaderButton onClick={handleYearMonthCalendarSwitch}>
                                    <WeekRangePicker
                                        autoOk
                                        disableToolbar
                                        selectedDate={selectedDate}
                                        startDate={startDate}
                                        endDate={endDate}
                                        variant="static"
                                        openTo="date"
                                        value={selectedDate}
                                        onChange={handleWeekRangeChange}
                                        onCalendarClick={handleWeekClick}
                                        isPreselection={isPreselection}
                                    />
                                    <InfoPanel message={getMessage(CODES.infoMessage)} />
                                </CalendarHeaderButton>
                            )}
                        </DatePickerContainer>
                    ) : (
                        <DatePickerContainer>
                            <DiscretRangePicker
                                value={activeTimeRange}
                                category={activeCategory}
                                onChange={handleDiscretRangeChange}
                            />
                        </DatePickerContainer>
                    )}
                </CategorizedDropdown.Category>
                <CategorizedDropdown.Category label={getMessage(CODES[TimeScale.MONTHLY])}>
                    {isCalendar ? (
                        <DatePickerContainer>
                            <TimeRangeButton
                                title={customTimeRangeButtonTitle}
                                tooltipTitle={customTimeRangeButtonTooltipTitle}
                                onClick={handleCloseTimeRangeClick}
                            />
                            <MonthRangePicker
                                startDate={startDate}
                                endDate={endDate}
                                onChange={handleMonthRangeChange}
                                isPreselection={isPreselection}
                            />
                        </DatePickerContainer>
                    ) : (
                        <DatePickerContainer>
                            <DiscretRangePicker
                                value={activeTimeRange}
                                category={activeCategory}
                                onChange={handleDiscretRangeChange}
                            />
                        </DatePickerContainer>
                    )}
                </CategorizedDropdown.Category>
            </CategorizedDropdown>
        </>
    );
};

export default withLocalization(GlobalDatePicker);
